/**
 * Simple GraphQL fetcher for React Query with axios and DataDog error logging
 * @module graphql-fetcher
 */
import { datadogRum } from '@datadog/browser-rum';
import axios from 'axios';

/**
 * Token manager to handle authentication tokens without module-scoped state
 * Prevents race conditions and SSR issues by encapsulating token management
 */
export class TokenManager {
  private token: string | null = null;

  /**
   * Get the current authentication token
   * @returns The current JWT token or null if not set
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Set the authentication token
   * @param token - The JWT token or null to clear
   */
  setToken(token: string | null): void {
    this.token = token;
  }

  /**
   * Clear the current authentication token
   */
  clear(): void {
    this.token = null;
  }
}

export const tokenManager = new TokenManager();

/**
 * Updates the token used for GraphQL requests
 * Called by ApolloAuthProvider when token changes
 * @param token - The JWT token or null to clear
 */
export const updateGraphQLToken = (token: string | null) => {
  tokenManager.setToken(token);
};

/**
 * Standard GraphQL response structure
 * @template TData - The shape of the data response
 */
interface GraphQLResponse<TData = any> {
  data?: TData;
  errors?: Array<{
    message: string;
    path?: Array<string | number>;
    locations?: Array<{ line: number; column: number }>;
    extensions?: Record<string, any>;
  }>;
}

/**
 * Creates a React Query–friendly GraphQL fetcher that executes the provided `query` with optional `variables`.
 *
 * The returned function performs a POST to `${process.env.CRYSTAL_API_URL}/graphql`, reads the current
 * auth token from `tokenManager` at request time (if present) and includes it as `Authorization: Bearer <token>`.
 * On GraphQL errors the function reports each error to DataDog and, if no `data` is present, throws an `Error`
 * augmented with `statusCode` and `graphQLErrors`. Network/axios errors are classified as `auth_error` (401/403)
 * or `network_error`, reported to DataDog, and rethrown as `Error` instances with `statusCode`. In development the
 * function logs additional diagnostic information to the console.
 *
 * @param variables - Optional variables object for the GraphQL operation.
 * @returns A zero-argument async function that executes the GraphQL request and resolves with the operation `data` of type `TData`.
 * @throws Error - If the GraphQL response contains errors and no `data` (error includes `statusCode` and `graphQLErrors`).
 * @throws Error - For network/axios errors (error includes `statusCode`; auth errors use status 401/403).
 */
export function graphqlFetcher<TData, TVariables extends Record<string, any>>(
  query: string,
  variables?: TVariables,
  _headers?: RequestInit['headers'],
): () => Promise<TData> {
  return async () => {
    const isDevelopment = process.env.NODE_ENV === 'development';

    const operationMatch = query.match(/(?:query|mutation|subscription)\s+(\w+)/);
    const operationName = operationMatch?.[1] || 'GraphQLOperation';
    const operationTypeMatch = query.match(/^\s*(query|mutation|subscription)/i);
    const operationType = operationTypeMatch?.[1]?.toLowerCase() || 'query';

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Get token at request time to avoid race conditions
    const currentToken = tokenManager.getToken();
    if (currentToken) {
      headers.Authorization = `Bearer ${currentToken}`;
    }

    try {
      const baseUrl = process.env.CRYSTAL_API_URL;
      if (!baseUrl) {
        const cfgErr = new Error('CRYSTAL_API_URL is not defined');
        if (typeof window !== 'undefined') {
          datadogRum.addError(cfgErr, { errorType: 'config_error' });
        }
        throw cfgErr;
      }
      const response = await axios.post<GraphQLResponse<TData>>(
        `${baseUrl.replace(/\/$/, '')}/graphql`,
        {
          query,
          variables,
        },
        {
          headers,
          timeout: 30000,
        },
      );

      const json = response.data;

      if (json.errors && json.errors.length > 0) {
        json.errors.forEach((error) => {
          if (isDevelopment) {
            console.group(`[GraphQL ${operationType.toUpperCase()}] ${operationName}`);
            console.error('Error:', error.message);
            if (error.path) console.log('Path:', error.path);
            if (error.extensions?.code) console.log('Code:', error.extensions.code);
            if (error.locations) console.log('Locations:', error.locations);
            console.groupEnd();
          }

          datadogRum.addError(new Error(`GRAPHQL-ERR - ${operationName}`), {
            message: error.message,
            operationName,
            operationType,
            errorType: 'graphql_error',
            errorCode: error.extensions?.code,
            errorPath: error.path,
            field: error.path?.[error.path.length - 1]?.toString(),
            statusCode: response.status,
          });
        });

        if (json.data) {
          if (isDevelopment) {
            console.warn(
              `[GraphQL ${operationType.toUpperCase()}] ${operationName}: Partial data returned despite errors`,
            );
          }
          return json.data;
        }

        const error = new Error(json.errors[0]?.message || 'GraphQL error');
        (error as any).statusCode = response.status;
        (error as any).graphQLErrors = json.errors;
        throw error;
      }

      if (!json.data) {
        throw new Error('No data returned from GraphQL query');
      }

      return json.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const statusCode = error.response?.status || 0;
        const isAuthError = statusCode === 401 || statusCode === 403;
        const errorType = isAuthError ? 'auth_error' : 'network_error';

        if (isDevelopment) {
          console.group(
            `[GraphQL ${operationType.toUpperCase()}] ${operationName} - Network Error`,
          );
          console.error(`Status: ${statusCode}`);
          console.error('Message:', error.message);
          if (error.response?.data) console.log('Response:', error.response.data);
          console.groupEnd();
        }

        datadogRum.addError(new Error(`GRAPHQL-ERR - ${operationName} - Network (${statusCode})`), {
          message: error.message,
          operationName,
          operationType,
          errorType,
          statusCode,
        });

        if (error.response?.data?.errors) {
          const graphQLError = new Error(error.response.data.errors[0]?.message || 'GraphQL error');
          (graphQLError as any).statusCode = statusCode;
          (graphQLError as any).graphQLErrors = error.response.data.errors;
          throw graphQLError;
        }

        const networkError = new Error(`Network error: ${error.message} (Status: ${statusCode})`);
        (networkError as any).statusCode = statusCode;
        throw networkError;
      }

      throw error;
    }
  };
}
